import { Button, cn } from '@teach/ui'
import hourglass from './assets/hourglass.svg'
import { PaymentCross } from './PaymentCross'
import { useCreditsDialog, type PendingPayment } from './CreditsDialogContext'
import { PaymentDone } from './PaymentDone'
import { openPaymentWindow } from './lib/openPaymentWindow'
import { isLivePhase, isManualPhase, type PaymentPhase } from './lib/paymentPhase'
import { useMessages } from '@/i18n'
import { messages } from './messages'

// Песочные часы переворачиваются на 180°, пауза, ещё 180° и пауза: это один
// цикл (в сумме полный оборот). При prefers-reduced-motion стоят неподвижно.
const FLIP_CSS = `
@keyframes hourglass-flip {
  0%, 20% { transform: rotate(0deg) }
  45%, 70% { transform: rotate(180deg) }
  95%, 100% { transform: rotate(360deg) }
}
.hourglass-flip { animation: hourglass-flip 4.4s cubic-bezier(0.5, 0, 0.3, 1) infinite }
@keyframes waiting-blink { 0%, 100% { opacity: 1 } 50% { opacity: 0.35 } }
.waiting-blink { animation: waiting-blink 1.8s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .hourglass-flip, .waiting-blink { animation: none } }
`

/**
 * Неуспешные терминальные исходы: опрос уже остановлен, и без кнопки автор
 * остался бы на экране без единого действия. `refunded` входит сюда наравне с
 * отменой и отказом — кредитов не прибавилось, значит выход тот же.
 */
const RETRYABLE: PaymentPhase[] = ['canceled', 'failed', 'refunded']

// `completed` тут нет намеренно: у успеха своя ветка ниже, а не строка в
// словаре. `idle` — состояние без транзакции, до этого шага не доходит.
type PhaseTextKey =
  | 'waiting'
  | 'paymentCanceled'
  | 'paymentFailed'
  | 'paymentRefunded'
  | 'paymentDegraded'
  | 'paymentExpired'

const TEXT: Partial<Record<PaymentPhase, PhaseTextKey>> = {
  waiting: 'waiting',
  canceled: 'paymentCanceled',
  failed: 'paymentFailed',
  refunded: 'paymentRefunded',
  degraded: 'paymentDegraded',
  expired: 'paymentExpired',
}

/**
 * Подпись выхода. Живой платёж бросают целиком — можно сменить и пакет, и
 * способ; после отказа автор обычно пробует другой способ; остальное — к пакетам.
 */
const backLabel = (phase: PaymentPhase): 'chooseAnother' | 'otherMethod' | 'toPackages' =>
  isLivePhase(phase) ? 'chooseAnother' : RETRYABLE.includes(phase) ? 'otherMethod' : 'toPackages'

/** Брошенная крипта всё равно дойдёт — предупреждаем, пока платёж живой. */
const warnsCrypto = (payment: PendingPayment | null, phase: PaymentPhase): boolean =>
  payment?.method === 'HELEKET' && isLivePhase(phase)

interface Props {
  /**
   * Назад к витрине: оплата брошена. Живую транзакцию снимает провайдер
   * (`abandonPayment`), наблюдение за ней кончается.
   */
  onBack: () => void
  /** Закрыть диалог совсем: единственное осмысленное действие после успеха. */
  onDone: () => void
}

export const WaitingPayment = ({ onBack, onDone }: Props) => {
  const { payment, watch } = useCreditsDialog()
  const m = useMessages(messages)

  // Успех — ОТДЕЛЬНЫЙ шаг мастера, а не строка внутри ожидания.
  if (watch.phase === 'completed') return <PaymentDone onDone={onDone} />

  const isRetryable = RETRYABLE.includes(watch.phase)
  // Ссылка на воронку живая, пока платёж не закончился отказом: и в ожидании
  // (попап мог не открыться), и в ручном режиме (опроса нет, а оплатить можно).
  const funnel = isRetryable ? null : payment?.paymentUrl

  return (
    <div className="flex flex-col items-center gap-4 pb-2 pt-8 text-center">
      {/* Крутилка только пока платёж реально ждём: у отказа и ручного режима она
          врала бы про идущий процесс. Серверную строку статуса в ожидании не печатаем: она
          приходит по-английски, а смысл фазы сказан по-русски ниже. */}
      {watch.phase === 'waiting' ? (
        <>
          <style>{FLIP_CSS}</style>
          <img src={hourglass} alt="" aria-hidden="true" className="hourglass-flip h-24 w-auto" />
        </>
      ) : null}
      {watch.phase === 'failed' || watch.phase === 'canceled' ? <PaymentCross /> : null}
      <p
        className={cn(
          'text-base text-muted-foreground',
          watch.phase === 'waiting' ? 'waiting-blink' : null,
        )}
      >
        {m[TEXT[watch.phase] ?? 'waiting']}
      </p>
      {isRetryable && watch.message ? (
        // Серверное пояснение — только там, где оно объясняет неуспех. В ожидании
        // оно приходило по-английски («Payment is being processed») и ничего не
        // добавляло к «Ждём подтверждения оплаты».
        <p className="text-sm text-destructive">{watch.message}</p>
      ) : null}
      <div className="flex flex-wrap justify-center gap-2">
        {funnel ? (
          <Button variant="outline" size="sm" onClick={() => openPaymentWindow(funnel)}>
            {m.reopenPayment}
          </Button>
        ) : null}
        {isManualPhase(watch.phase) ? (
          <Button variant="outline" size="sm" onClick={watch.refresh}>
            {m.refreshBalance}
          </Button>
        ) : null}
        {/* Выход есть в любой фазе (запрос CEO, 10.2026): брошенная оплата не
            должна запирать диалог. Крестик, в отличие от этой кнопки, платёж не
            бросает — наблюдение идёт, успех доедет тостом. */}
        <Button variant="outline" size="sm" onClick={() => onBack()}>
          {m[backLabel(watch.phase)]}
        </Button>
      </div>
      {/* Инвойс Heleket у провайдера не отменить: отправленная крипта дойдёт и
          после выхода. Автору, который уже отправил, уходить незачем. */}
      {warnsCrypto(payment, watch.phase) ? (
        <p className="text-sm text-muted-foreground">{m.cryptoAbandonNote}</p>
      ) : null}
    </div>
  )
}
